import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChatBubbleEmpty,
  FastArrowLeft,
  FastArrowRight,
  MultiBubble,
  NavArrowLeft,
  NavArrowRight,
  Search,
} from 'iconoir-react';
import { Loader2 } from 'lucide-react';

import { ConversationContextMenu } from 'components/blocks/layout/conversation-context-menu';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';
import type { SidebarItem } from 'components/blocks/layout/types/sidebar-conversation';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useConversationContextMenu } from 'features/chat/hooks/use-conversation-context-menu';
import { useConfig } from 'hooks/use-config';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { getConversationDisplayTitle } from 'utils/chat-utilities';
import type { modalOption } from '/models/sidebar';

import { formatChatHistoryDate } from './utils';

const CHAT_HISTORY_PAGE_SIZE = 20;

const chatHistoryPaginationButtonClassName =
  'h-8 w-8 rounded-full border border-[#e7e7e7] bg-white p-0 text-[#3d3d3d] shadow-none hover:bg-white hover:text-[#3d3d3d] disabled:border-[#e7e7e7] disabled:bg-white disabled:text-[#c4c4c4] dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-card dark:disabled:border-border dark:disabled:bg-card dark:disabled:text-muted-foreground';

const chatHistoryRowTextClassName =
  'font-sans text-sm font-normal not-italic leading-[1.6] tracking-normal text-[#4f4f4f] dark:text-foreground sm:text-base';

const chatHistoryHeaderTextClassName =
  'font-sans text-sm font-bold not-italic leading-[1.2] tracking-normal text-[#4f4f4f] dark:text-muted-foreground';

const desktopTableGridClassName =
  'grid-cols-[minmax(220px,2fr)_minmax(120px,1fr)_auto] lg:grid-cols-[469px_minmax(0,1fr)_auto]';

function ChatHistoryRowIcon({ item }: { item: SidebarItem }) {
  const iconClassName = 'shrink-0 text-grey-900';

  // if (item.ProjectId) {
  //   return (
  //     <div className="flex shrink-0 items-center rounded-[8px] bg-[#f9ffc2] p-2 dark:bg-brand-100/20">
  //       <ProjectFolderIcon />
  //     </div>
  //   );
  // }

  // const Icon = item.IsGroup ? MultiBubble : ChatBubbleEmpty;

  const Icon = ChatBubbleEmpty;

  return (
    <div className="flex shrink-0 items-center rounded-[8px] bg-[#f9ffc2] p-2 dark:bg-brand-100/20">
      <Icon width={20} height={20} strokeWidth={1.75} className={iconClassName} />
    </div>
  );
}

type ChatHistoryRowProps = {
  item: SidebarItem;
  title: string;
  onOpen: () => void;
  menuOptions: modalOption[];
  moreActionsLabel: string;
};

function ChatHistoryRow({
  item,
  title,
  onOpen,
  menuOptions,
  moreActionsLabel,
}: ChatHistoryRowProps) {
  const formattedDate = formatChatHistoryDate(item.LastUpdatedDate);

  return (
    <>
      <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 border-b border-solid border-[#f6f6f6] pb-3 dark:border-border/60 md:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 items-center gap-3 text-left"
        >
          <ChatHistoryRowIcon item={item} />
          <span className={`min-w-0 flex-1 truncate ${chatHistoryRowTextClassName}`}>{title}</span>
          <span className={`max-w-[42%] shrink-0 truncate ${chatHistoryRowTextClassName}`}>
            {formattedDate}
          </span>
        </button>

        <ConversationContextMenu
          variant="page"
          contentSide="bottom"
          contentAlign="end"
          triggerAriaLabel={moreActionsLabel}
          options={menuOptions}
        />
      </div>

      <div
        className={`hidden w-full min-w-0 items-center gap-x-4 border-b border-solid border-[#f6f6f6] pb-4 dark:border-border/60 md:grid ${desktopTableGridClassName}`}
      >
        <button
          type="button"
          onClick={onOpen}
          className="flex w-[247px] items-center gap-4 text-left"
        >
          <ChatHistoryRowIcon item={item} />
          <span className={`w-[200px] truncate ${chatHistoryRowTextClassName}`}>{title}</span>
        </button>

        <p className={`min-w-0 truncate ${chatHistoryRowTextClassName}`}>{formattedDate}</p>

        <div className="flex justify-end">
          <ConversationContextMenu
            variant="page"
            contentSide="bottom"
            contentAlign="end"
            triggerAriaLabel={moreActionsLabel}
            options={menuOptions}
          />
        </div>
      </div>
    </>
  );
}

export default function ChatHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSearchResults = useChatStore((state) => state.fetchSearchResults);
  const conversations = useChatStore((state) => state.searchConversationsList);
  const totalCount = useChatStore((state) => state.searchConversationsTotalCount);
  const { getMenuOptions, conversationMenuDialogs } = useConversationContextMenu();

  const pageCount = Math.max(1, Math.ceil(totalCount / CHAT_HISTORY_PAGE_SIZE));

  useEffect(() => {
    if (!isLoading && totalCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, isLoading, totalCount]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      await fetchSearchResults(search, configurationSettings, {
        page,
        pageSize: CHAT_HISTORY_PAGE_SIZE,
      });
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [search, page, fetchSearchResults, configurationSettings]);

  const filteredConversations = useMemo(
    () => conversations.filter((item) => !item.AgentId),
    [conversations]
  );

  const handleOpenConversation = (item: SidebarItem) => {
    if (item.ProjectId) {
      navigate(`/chat/${item.ConversationId}/${item.ProjectId}`);
      return;
    }

    navigate(`/chat/${item.ConversationId}`);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto py-1 pb-6">
      <div className="mx-auto flex w-full min-w-0 max-w-[829px] flex-col gap-6 md:gap-10 lg:gap-[60px]">
        <div className="flex w-full flex-col gap-6 md:gap-8">
          <div className="flex w-full items-center justify-between">
            <h1 className="shrink-0 font-sans text-xl font-medium not-italic leading-[1.2] tracking-normal text-grey-900 dark:text-foreground sm:text-[24px]">
              {t('CHAT_HISTORY', 'Chat History')}
            </h1>
          </div>

          <div className="flex h-12 w-full items-center gap-3 rounded-[8px] border border-solid border-[#e7e7e7] bg-white px-4 py-3 shadow-[0px_10px_10px_rgba(173,174,163,0.11)] dark:border-grey-400 dark:bg-card sm:gap-4 sm:px-5 sm:py-4 md:h-[54px]">
            <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden p-1">
              <Search
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-[#888888] dark:text-grey-400"
              />
            </div>
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t('SEARCH_CHATS', 'Search chats...')}
              className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-sm font-normal not-italic leading-[1.6] tracking-normal text-grey-900 shadow-none placeholder:text-[#888888] focus-visible:ring-0 dark:text-foreground sm:text-base"
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 md:gap-6">
          <div className={`md:hidden ${chatHistoryHeaderTextClassName}`}>{t('CHAT', 'Chat')}</div>

          <div className={`hidden w-full min-w-0 items-center gap-x-4 md:grid ${desktopTableGridClassName}`}>
            <p className={chatHistoryHeaderTextClassName}>{t('CHAT', 'Chat')}</p>
            <p className={chatHistoryHeaderTextClassName}>{t('DATE', 'Date')}</p>
            <span className="w-6" aria-hidden />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-true-white" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <p className="py-8 font-sans text-sm font-normal leading-[1.6] text-[#888888] dark:text-muted-foreground sm:text-base">
              {t('NO_RESULTS_FOUND', 'No results found')}
            </p>
          ) : (
            <>
              {filteredConversations.map((item) => {
                const title = getConversationDisplayTitle(
                  item.ConversationTitle,
                  t('NEW_CHAT', 'New chat')
                );

                return (
                  <ChatHistoryRow
                    key={item.ConversationId}
                    item={item}
                    title={title}
                    onOpen={() => handleOpenConversation(item)}
                    menuOptions={getMenuOptions(item.ConversationId, title, item.IsGroup)}
                    moreActionsLabel={t('MORE_ACTIONS', 'More actions')}
                  />
                );
              })}

              {totalCount > 0 ? (
                <div className="mt-2 flex w-full items-center justify-end gap-4 md:mt-6">
                  <p className="text-sm font-normal leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                    {t('PAGE_OF', {
                      page,
                      total: pageCount,
                      defaultValue: `Page ${page} of ${pageCount}`,
                    })}
                  </p>

                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={chatHistoryPaginationButtonClassName}
                      onClick={() => setPage(1)}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_FIRST_PAGE', 'Go to first page')}
                    >
                      <FastArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={chatHistoryPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_PREVIOUS_PAGE', 'Go to previous page')}
                    >
                      <NavArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={chatHistoryPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_NEXT_PAGE', 'Go to next page')}
                    >
                      <NavArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={chatHistoryPaginationButtonClassName}
                      onClick={() => setPage(pageCount)}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_LAST_PAGE', 'Go to last page')}
                    >
                      <FastArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {conversationMenuDialogs}
      </div>
    </div>
  );
}
